import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";

const STATUS = { pending: "bg-amber-50 text-amber-900", approved: "bg-green-50 text-green-900", rejected: "bg-red-50 text-red-900" };
const smallBtn = "text-xs uppercase tracking-wider transition-colors duration-200";

const Panel = ({ title, hint, children, testId }) => (
  <section className="mt-8 border border-border bg-card" data-testid={testId}>
    <div className="px-6 py-4 border-b border-border">
      <p className="label-caps">{title}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
    {children}
  </section>
);

export default function AdminCompliance() {
  const [reqs, setReqs] = useState([]);
  const [ret, setRet] = useState(null);

  const load = () => {
    api.get("/admin/deletion-requests").then(({ data }) => setReqs(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/retention").then(({ data }) => setRet(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const decide = (r, action) => {
    const reason = window.prompt(action === "approve" ? "Note for the record (optional)" : "Reason for declining") ?? null;
    if (reason === null && action === "reject") return;
    act(() => api.post(`/admin/deletion-requests/${r.id}/decide`, { action, reason }), `Request ${action}d`);
  };

  return (
    <div data-testid="admin-compliance-page">
      <PageHeader eyebrow="Admin" title="Compliance · DPDP" testId="admin-compliance-header"
        description="Data deletion requests, one-year retention flags after archiving, and purge of uploaded files from cloud storage."
        actions={
          <Button onClick={() => act(() => api.post("/admin/retention/run"), "Retention sweep run")} data-testid="run-retention-button"
            className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-5">
            Run retention sweep
          </Button>
        } />

      <Panel title="Data deletion requests" testId="deletion-requests-panel"
        hint="Approving erases the user's name, e-mail, phone and credentials; case dockets stay intact with the identity anonymised.">
        {reqs.length === 0 ? <p className="p-8 text-sm text-muted-foreground" data-testid="deletion-requests-empty">No requests.</p> : reqs.map((r) => (
          <div key={r.id} className="px-6 py-4 border-b border-border last:border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`deletion-request-${r.id}`}>
            <div>
              <p className="text-sm font-medium">{r.user_name} <span className="text-muted-foreground">({r.role}) · {r.user_email}</span></p>
              <p className="text-xs text-muted-foreground mt-1">{r.reason} · {fmtDateTime(r.created_at)}</p>
              {r.decided_at && <p className="text-xs mt-1">{r.decided_by}: {r.decision_reason || "—"} · {fmtDateTime(r.decided_at)}</p>}
            </div>
            <div className="flex items-center gap-4">
              <Badge className={STATUS[r.status]}>{r.status}</Badge>
              {r.status === "pending" && (
                <>
                  <button onClick={() => decide(r, "approve")} data-testid={`approve-deletion-${r.id}`} className={`${smallBtn} text-red-800 hover:text-red-600`}>Approve & erase</button>
                  <button onClick={() => decide(r, "reject")} data-testid={`reject-deletion-${r.id}`} className={`${smallBtn} text-blue-900 dark:text-blue-400`}>Decline</button>
                </>
              )}
            </div>
          </div>
        ))}
      </Panel>

      <Panel title="Retention review · inactive users" testId="retention-users-panel"
        hint={ret ? `Participants with no sign-in since a reference was archived ${ret.retention_days} days ago. ${ret.erased_users} account(s) erased to date.` : ""}>
        {!ret ? <div className="h-20 animate-pulse bg-muted" /> : ret.flagged_users.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground" data-testid="retention-users-empty">No account is flagged.</p>
        ) : ret.flagged_users.map((u) => (
          <div key={u.id} className="px-6 py-4 border-b border-border last:border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`retention-user-${u.id}`}>
            <div>
              <p className="text-sm font-medium">{u.name} <span className="text-muted-foreground">({u.role}) · {u.email}</span></p>
              <p className="text-xs text-muted-foreground mt-1">
                Flagged {fmtDate(u.flagged_at)} · {u.case_ref} · last sign-in {u.last_login_at ? fmtDate(u.last_login_at) : "never"}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge className="bg-amber-50 text-amber-900">inactive · retention review</Badge>
              <button data-testid={`anonymise-user-${u.id}`} className={`${smallBtn} text-red-800 hover:text-red-600`}
                onClick={() => window.confirm(`Erase personal data of ${u.name}?`) && act(() => api.post(`/admin/users/${u.id}/anonymise`), "Personal data erased")}>
                Erase data
              </button>
            </div>
          </div>
        ))}
      </Panel>

      <Panel title="Cloud purge · archived over a year" testId="retention-cases-panel"
        hint="Deletes every uploaded file of the reference from AWS S3. The docket, orders text and audit trail remain.">
        {!ret ? <div className="h-20 animate-pulse bg-muted" /> : ret.purgeable_cases.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground" data-testid="retention-cases-empty">No archived reference has crossed the retention period.</p>
        ) : ret.purgeable_cases.map((c) => (
          <div key={c.id} className="px-6 py-4 border-b border-border last:border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`purge-case-${c.ref_id}`}>
            <div>
              <p className="text-sm font-medium font-mono">{c.case_number || c.ref_id} <span className="font-sans text-muted-foreground">· {c.title}</span></p>
              <p className="text-xs text-muted-foreground mt-1">Archived {fmtDate(c.archived_at)} · {c.documents} file(s) · {Math.round(c.bytes / 1024)} KB</p>
            </div>
            {c.documents_purged_at ? (
              <Badge className="bg-slate-100 text-slate-800">purged {fmtDate(c.documents_purged_at)}</Badge>
            ) : (
              <button data-testid={`purge-case-button-${c.ref_id}`} className={`${smallBtn} text-red-800 hover:text-red-600`}
                onClick={() => window.confirm(`Delete all ${c.documents} uploaded file(s) of ${c.ref_id} from the cloud? This cannot be undone.`) &&
                  act(() => api.post(`/admin/cases/${c.id}/purge-documents`), "Files deleted from cloud storage")}>
                Delete uploaded data
              </button>
            )}
          </div>
        ))}
      </Panel>
    </div>
  );
}
